// Import package components
import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { Image, Flex } from "uikit-react";
import { useCookies } from "react-cookie";
import classnames from "classnames";
import moment from "moment";
import UIkit from "uikit";

import { Link } from "../../ui";
import { AppContexts } from "../../providers";

import RecordsPerPage from "../common/RecordsPerPage";
import Pagination from "../common/Pagination";
import ChatbotFaqsAdd from "./add";
import Dialog from "../common/Dialog";

const ChatbotFaqs = () => {
  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage } = cookies;
  const {
    loading,
    faqs,
    templates,
    listFaqs,
    createFaq,
    listTemplates,
    deleteFaq,
    removeTemplate,
    setLoading,
  } = useContext(AppContexts.ChatbotContext);

  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [deleteId, setDeleteId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [pending, setPending] = useState(null);

  // Add debounce for search
  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      await loadFaqs();
      setLoading(false);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadFaqs = useCallback(async () => {
    try {
      const response = await listFaqs(
        searchTerm,
        sortBy,
        sortOrder,
        page,
        recordsPerPage,
      );
      if (response) {
        setTotal(response.total);
      }
    } catch (error) {
      console.error("Error loading FAQs:", error);
      setTotal(0);
    }
  }, [searchTerm, sortBy, sortOrder, page, recordsPerPage]);

  useEffect(() => {
    listTemplates();
  }, []);

  useEffect(() => {
    document.title = "AI Chatbot FAQs - RueBaRue";
  }, []);

  // Load FAQs when any filter changes
  useEffect(() => {
    setLoading(true);
    loadFaqs().finally(() => setLoading(false));
  }, [searchTerm, sortBy, sortOrder, page, recordsPerPage]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPage(0); // Reset to first page on search
  };

  const handleDelete = useCallback(
    async (id) => {
      try {
        await deleteFaq(id);
        await loadFaqs();
        UIkit.notification({
          message: "FAQ deleted successfully",
          status: "success",
        });
      } catch (error) {
        console.error("Error deleting FAQ:", error);
        UIkit.notification({
          message: "Failed to delete FAQ",
          status: "danger",
        });
      }
    },
    [deleteFaq, loadFaqs],
  );

  const handleFaqUpdate = useCallback(() => {
    loadFaqs();
    setEditing(null);
  }, [loadFaqs]);

  const openDeleteModal = (id) => {
    setDeleteId(id);
    UIkit.modal("#faq-delete-modal").show();
  };

  const openDeleteTemplateModal = (id) => {
    console.log("ID:", id);
    setDeleteId(id);
    UIkit.modal("#template-delete-modal").show();
  };

  const closeDeleteModal = () => {
    setDeleteId(null);
    UIkit.modal("#faq-delete-modal").hide();
  };

  const closeDeleteTemplateModal = () => {
    setDeleteId(null);
    UIkit.modal("#template-delete-modal").hide();
  };

  const openEditFaq = (faq) => {
    setEditing(faq);
  };

  const openTemplate = (template) => {
    setEditing({
      template_id: template.id,
      question: template.question,
      answer: template.answer,
    });
  };

  const copyTemplate = async (template) => {
    setPending(template.id);

    await createFaq({
      question: template.question.trim(),
      answer: template.answer.trim(),
      template_id: template.id || null,
    });

    listFaqs();
    listTemplates();

    UIkit.notification({
      message: "Template saved to your account",
      status: "success",
    });

    setPending(null);
  };

  const deleteTemplate = async () => {
    if (deleteId === null) {
      return;
    }

    setPending(deleteId);

    await removeTemplate(deleteId);

    listFaqs();
    listTemplates();

    UIkit.notification({
      message: "Template removed from your account",
      status: "success",
    });

    setPending(null);
    closeDeleteTemplateModal();
  };

  const faqList = useMemo(() => {
    let joined = [...faqs, ...templates];

    if (sortBy === "updated_at") {
      joined = joined.sort((a, b) => {
        return moment(a).valueOf() - moment(b).valueOf();
      });
    } else {
      joined = joined.sort((a, b) => {
        return a.question.localeCompare(b.question);
      });
    }

    return joined.slice(page * recordsPerPage, recordsPerPage);
  }, [faqs, templates]);

  return (
    <>
      <section id="chatbot-faqs">
        <div id="hms-page-title">
          <h1 className="uk-heading-small">AI Chatbot FAQs</h1>
        </div>
        <div id="hms-main-body">
          <div className="uk-flex uk-flex-between uk-flex-top">
            <div className="hms-guest-btn">
              <button
                className="uk-button hms-btn hms-red-btn"
                uk-toggle="target: #add-ai-faq"
              >
                <span uk-icon="icon: plus; ratio: 0.7"></span> FAQ
              </button>
            </div>
          </div>
          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              <Flex alignment="between top" className="guest-form-top-filters">
                {faqs?.length > 0 && (
                  <>
                    <div>
                      <form
                        action=""
                        className="uk-form uk-flex uk-flex-middle hms-form"
                        >
                        <div className="uk-inline">
                          <div className="uk-search uk-search-default uk-form-custom uk-margin-small-right">
                            {searchTerm && searchTerm !== "" ? (
                              <a
                                className="uk-form-icon-flip uk-form-icon"
                                uk-icon="icon: close"
                                onClick={() => {
                                  setSearchTerm("");
                                  setPage(0);
                                }}
                              ></a>
                            ) : (
                              <span
                                className="uk-search-icon-flip"
                                uk-search-icon="true"
                              />
                            )}
                            <input
                              className="uk-search-input"
                              type="search"
                              placeholder="FAQ"
                              value={searchTerm}
                              onChange={handleSearchChange}
                            />
                          </div>
                        </div>
                      </form>
                    </div>
                    <div>
                      <div className="hms-sortby-btn">
                        <button className="uk-button" type="button">
                          SORT BY<span uk-icon="icon: triangle-down"></span>
                        </button>
                        <div uk-dropdown="mode: click; offset: 0; animation: uk-animation-slide-top-small; duration: 400">
                          <ul className="uk-nav uk-dropdown-nav">
                            <li
                              className={classnames({
                                "uk-active": sortBy === "updated_at",
                              })}
                            >
                              <Link
                                href="#"
                                onClick={() => {
                                  setSortBy("updated_at");
                                  setSortOrder("desc");
                                  setPage(0);
                                }}
                              >
                                Last updated
                              </Link>
                            </li>
                            <li
                              className={classnames({
                                "uk-active": sortBy === "question",
                              })}
                            >
                              <Link
                                href="#"
                                onClick={() => {
                                  setSortBy("question");
                                  setSortOrder("asc");
                                  setPage(0);
                                }}
                              >
                                Question (A-Z)
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </Flex>

              {(faqs || []).length === 0 ? (
                <div className="uk-alert-primary uk-margin-small-top">
                  To get your AI Chatbot answering guest questions, add FAQs from our{" "}
                  standard templates. Use <strong> Add</strong> to save, 
                  <strong> Edit</strong> to personalize, 
                  or <strong> Delete</strong> to skip.
                </div>                
              ) : null}

              <table className="uk-table uk-table-hover uk-table-divider hms-table faq-table">
                <thead>
                  <tr>
                    <th className="uk-table-expand">Question</th>
                    <th className="uk-table-expand">Answer</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={3} className="uk-text-center">
                        <div uk-spinner=""></div>
                        Loading...
                      </td>
                    </tr>
                  ) : faqList.length > 0 ? (
                    <>
                      {faqList.map((faq) => {
                        return !faq.category ? (
                          <tr key={faq.id}>
                            <td>{faq.question}</td>
                            <td>{faq.answer}</td>
                            <td className="hms-action-btn">
                              <div className="uk-flex uk-flex-top">
                                <div className="uk-inline">
                                  <button
                                    className="uk-button"
                                    uk-toggle="target: #add-ai-faq"
                                    uk-tooltip="Edit"
                                    onClick={() => {
                                      openEditFaq(faq);
                                    }}
                                  >
                                    <Image src="/images/edit.svg" />
                                  </button>
                                </div>
                                <div className="uk-inline">
                                  <button
                                    className="uk-button"
                                    type="button"
                                    uk-tooltip="Delete"
                                    onClick={() => openDeleteModal(faq.id)}
                                  >
                                    <img src="/images/delete.svg" />
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr key={faq.id}>
                            <td>
                              {faq.question}{" "}
                              <div className="hms-booking-tag">Template</div>
                            </td>
                            <td>{faq.answer}</td>
                            <td
                              className={classnames({
                                "hms-action-btn": true,
                                disabled: pending === faq.id,
                              })}
                            >
                              <div className="uk-flex uk-flex-top">
                                <div className="uk-inline">
                                  <button
                                    className="uk-button"
                                    uk-tooltip="Save to my Account"
                                    onClick={() => {
                                      copyTemplate(faq);
                                    }}
                                  >
                                    <Image src="/images/add.svg" />
                                  </button>
                                </div>
                                <div className="uk-inline">
                                  <button
                                    className="uk-button"
                                    uk-toggle="target: #add-ai-faq"
                                    uk-tooltip="Edit and Save"
                                    onClick={() => {
                                      openTemplate(faq);
                                    }}
                                  >
                                    <Image src="/images/edit.svg" />
                                  </button>
                                </div>
                                <div className="uk-inline">
                                  <button
                                    className="uk-button"
                                    type="button"
                                    uk-tooltip="Delete"
                                    onClick={() =>
                                      openDeleteTemplateModal(faq.id)
                                    }
                                  >
                                    <img src="/images/delete.svg" />
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  ) : (
                    <tr>
                      <td colSpan={3} className="uk-text-left">
                        No FAQs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Flex alignment="between top" className="guest-form-top-filters">
            {total > 0 && (
              <>
                <Pagination count={total} index={page} onIndexChanged={setPage} />
                {total > 0 && <RecordsPerPage />}
              </>
            )}
          </Flex>       
        </div>
      </section>

      <ChatbotFaqsAdd data={editing} onSuccess={handleFaqUpdate} />

      <Dialog id="faq-delete-modal">
        <div className="uk-modal-dialog uk-modal-body">
          <button
            className="uk-modal-close-default"
            type="button"
            uk-close="true"
            onClick={closeDeleteModal}
          ></button>
          <h2 className="uk-modal-title">Delete FAQ</h2>
          <p>Are you sure you want to delete this FAQ?</p>
          <p className="uk-text-right">
            <button
              className="uk-button hms-btn uk-modal-close uk-modal-delete-cancel"
              type="button"
              onClick={closeDeleteModal}
            >
              Cancel
            </button>
            <button
              className="uk-button hms-btn hms-gray-btn"
              type="button"
              onClick={async () => {
                await handleDelete(deleteId);
                closeDeleteModal();
              }}
              disabled={loading}
            >
              {loading ? <div data-uk-spinner="ratio: 0.5;"></div> : "Delete"}
            </button>
          </p>
        </div>
      </Dialog>

      <Dialog id="template-delete-modal">
        <div className="uk-modal-dialog uk-modal-body">
          <button
            className="uk-modal-close-default"
            type="button"
            uk-close="true"
            onClick={deleteTemplate}
          ></button>
          <h2 className="uk-modal-title">Delete Template</h2>
          <p>Are you sure you want delete this Template?</p>
          <p className="uk-text-right">
            <button
              className="uk-button hms-btn uk-modal-close uk-modal-delete-cancel"
              type="button"
              onClick={closeDeleteTemplateModal}
            >
              Cancel
            </button>
            <button
              className="uk-button hms-btn hms-gray-btn"
              type="button"
              onClick={async () => {
                await deleteTemplate(deleteId);
                closeDeleteTemplateModal();
              }}
              disabled={loading}
            >
              {loading ? <div data-uk-spinner="ratio: 0.5;"></div> : "Delete"}
            </button>
          </p>
        </div>
      </Dialog>
    </>
  );
};

export default ChatbotFaqs;
